import type {ChatMessageDesignPart, ChatMessagePart, ChatMessageUserImagePart} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import type {AttachmentVideoSubject, VideoSubject} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {VideoSubjectKind} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';

/**
 * Names a user's attachment in the video creation state. The server defines the key and sends it on every part it serves.
 * The derivation below exists only for an optimistic part the server has not served yet, and must match the PHP attachmentKey() (pinned by tests/fixtures/attachment-keys.json).
 */
export const attachmentKeyOf = (part: ChatMessageUserImagePart | ChatMessageDesignPart): string => {
  if (part.attachmentKey !== undefined) {
    return part.attachmentKey;
  }

  if (part.type === ChatMessagePartType.USER_IMAGE) {
    return part.hashedFilename;
  }

  return `${part.hashedId}:${part.pageHashedId ?? ''}`;
};

/** The chat asset "create more" animates: an upload is its own asset; a design has none, so it is not offered. */
export const getRegenerationImageHashedId = (part: ChatMessageUserImagePart | ChatMessageDesignPart): string | null => {
  return part.type === ChatMessagePartType.USER_IMAGE ? part.hashedFilename : null;
};

export const isVideoAttachmentPart = (part: ChatMessagePart): part is ChatMessageUserImagePart | ChatMessageDesignPart => {
  return part.type === ChatMessagePartType.USER_IMAGE || part.type === ChatMessagePartType.DESIGN;
};

/** Names the subject's video creation state in the chat. */
export const getVideoCreationStateKey = (videoSubject: VideoSubject): string => {
  return videoSubject.kind === VideoSubjectKind.ATTACHMENT ? videoSubject.attachmentKey : videoSubject.imageHashedId;
};

/** The chat asset "create more" animates; null when the subject has none to offer. */
export const getVideoSubjectRegenerationImageHashedId = (videoSubject: VideoSubject): string | null => {
  return videoSubject.kind === VideoSubjectKind.ATTACHMENT ? videoSubject.regenerationImageHashedId : videoSubject.imageHashedId;
};

/** A video made from a user's attachment is saved in the chat; one made from a generated image is not. */
export const getChatIdToSaveVideoIn = (chatId: string, videoSubject: VideoSubject): string | undefined => {
  return videoSubject.kind === VideoSubjectKind.ATTACHMENT ? chatId : undefined;
};

export const getAttachmentVideoSubject = (part: ChatMessageUserImagePart | ChatMessageDesignPart): AttachmentVideoSubject => {
  return {kind: VideoSubjectKind.ATTACHMENT, attachmentKey: attachmentKeyOf(part), regenerationImageHashedId: getRegenerationImageHashedId(part)};
};
